import type { Persona } from '@/components/ui/PersonaCard';

// ============================================================================
// CONSUMER PROFILE TYPES
// ============================================================================

/**
 * User selections for building a consumer profile
 *
 * INPUTS ONLY: 11 demographic variables + soda consumption + channel
 * Price sensitivity and brand loyalty are OUTPUTS derived from the profile
 */
export interface ProfileSelections {
  // === DEMOGRAPHIC VARIABLES (11) ===
  ageRanges: string[];              // Multi-select
  gender: string;
  incomeLevel: string;              // NSE (socioeconomic level) - includes 'all'
  locationTypes: string[];          // Multi-select
  region: string;
  householdSize: string;
  hasChildren: string;              // 'yes' | 'no' | 'all' - dropdown
  educationLevel: string;
  occupation: string;
  maritalStatus: string;
  lifestyleSegment: string;

  // === SODA CONSUMPTION ===
  consumptionFrequency: string;
  brandPreference: string;

  // === CHANNEL ===
  channelPreference: string;
}

/**
 * A persona derived from user selections (not from raw segment data)
 */
export interface DerivedPersona extends Persona {
  derived: true;
  matchedSegment: string;
  matchedSegmentId: string;
  matchScore: number;  // 0-1, how well selections match the segment
  selections: ProfileSelections;
  derivedMetrics: {
    estimatedShare: number;
    purchaseFrequency: string;
    avgBasketSize: string;
    channelPreference: string;
  };
  // Calculated outputs (derived from demographic inputs)
  calculatedPriceSensitivity: number;  // 0-1
  calculatedBrandLoyalty: number;      // 0-1
  // Targeting information (derived from demographic inputs)
  targetingInfo: {
    keyFactors: Array<{ factor: string; importance: number }>; // Top factors they care about
    mediaChannels: Array<{ channel: string; affinity: number }>; // Where to reach them
    bestTimeToReach: string;
    messagingTips: string[];
  };
}

/**
 * Segment data structure (matches YAML structure)
 */
export interface Segment {
  id: string;
  name: string;
  share: number;
  characteristics: string[];
  price_sensitivity: number;
  brand_loyalty: number;
  demographics: {
    age_range: string;
    income: string;
    geography: string;
  };
}

/**
 * Comparison result between two profiles
 */
export interface ComparisonResult {
  priceSensitivityDiff: number;  // A - B
  brandLoyaltyDiff: number;
  sameSegment: boolean;
  keyDifferences: string[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const AGE_RANGES = [
  { value: '18-28', label: '18-28' },
  { value: '25-35', label: '25-35' },
  { value: '35-45', label: '35-45' },
  { value: '45-55', label: '45-55' },
  { value: '55+', label: '55+' },
];

export const INCOME_LEVELS = [
  { value: 'all', label: 'All levels' },
  { value: 'D+', label: 'D+' },
  { value: 'D', label: 'D' },
  { value: 'C-', label: 'C-' },
  { value: 'C', label: 'C' },
  { value: 'C+', label: 'C+' },
  { value: 'B', label: 'B' },
  { value: 'A/B', label: 'A/B' },
];

export const LOCATION_TYPES = [
  { value: 'urban', label: 'Urban' },
  { value: 'suburban', label: 'Suburban' },
  { value: 'rural', label: 'Rural' },
  { value: 'urban_periphery', label: 'Urban Periphery' },
];

// Spanish translations
export const AGE_RANGES_ES = [
  { value: '18-28', label: '18-28 años' },
  { value: '25-35', label: '25-35 años' },
  { value: '35-45', label: '35-45 años' },
  { value: '45-55', label: '45-55 años' },
  { value: '55+', label: '55+ años' },
];

export const INCOME_LEVELS_ES = [
  { value: 'all', label: 'Todos los niveles' },
  { value: 'D+', label: 'D+ (Bajo)' },
  { value: 'D', label: 'D (Bajo)' },
  { value: 'C-', label: 'C- (Medio-bajo)' },
  { value: 'C', label: 'C (Medio)' },
  { value: 'C+', label: 'C+ (Medio-alto)' },
  { value: 'B', label: 'B (Alto)' },
  { value: 'A/B', label: 'A/B (Premium)' },
];

// === CHILDREN OPTIONS ===

export const HAS_CHILDREN = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const HAS_CHILDREN_ES = [
  { value: 'all', label: 'Todos' },
  { value: 'yes', label: 'Sí' },
  { value: 'no', label: 'No' },
];

export const LOCATION_TYPES_ES = [
  { value: 'urban', label: 'Urbano' },
  { value: 'suburban', label: 'Suburbano' },
  { value: 'rural', label: 'Rural' },
  { value: 'urban_periphery', label: 'Periferia urbana' },
];

// === ADDITIONAL DEMOGRAPHICS ===

export const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'all', label: 'All' },
];

export const GENDERS_ES = [
  { value: 'male', label: 'Masculino' },
  { value: 'female', label: 'Femenino' },
  { value: 'all', label: 'Todos' },
];

export const REGIONS = [
  { value: 'north', label: 'North' },
  { value: 'center', label: 'Center' },
  { value: 'south', label: 'South' },
  { value: 'west', label: 'West' },
  { value: 'national', label: 'National' },
];

export const REGIONS_ES = [
  { value: 'north', label: 'Norte' },
  { value: 'center', label: 'Centro' },
  { value: 'south', label: 'Sur' },
  { value: 'west', label: 'Occidente' },
  { value: 'national', label: 'Nacional' },
];

export const HOUSEHOLD_SIZES = [
  { value: '1', label: '1 person' },
  { value: '2', label: '2 people' },
  { value: '3-4', label: '3-4 people' },
  { value: '5+', label: '5+ people' },
  { value: 'all', label: 'All sizes' },
];

export const HOUSEHOLD_SIZES_ES = [
  { value: '1', label: '1 persona' },
  { value: '2', label: '2 personas' },
  { value: '3-4', label: '3-4 personas' },
  { value: '5+', label: '5+ personas' },
  { value: 'all', label: 'Todos' },
];

export const EDUCATION_LEVELS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'preparatory', label: 'High School' },
  { value: 'university', label: 'University' },
  { value: 'postgraduate', label: 'Postgraduate' },
  { value: 'all', label: 'All levels' },
];

export const EDUCATION_LEVELS_ES = [
  { value: 'primary', label: 'Primaria' },
  { value: 'secondary', label: 'Secundaria' },
  { value: 'preparatory', label: 'Preparatoria' },
  { value: 'university', label: 'Universidad' },
  { value: 'postgraduate', label: 'Posgrado' },
  { value: 'all', label: 'Todos' },
];

export const OCCUPATIONS = [
  { value: 'student', label: 'Student' },
  { value: 'employee', label: 'Employee' },
  { value: 'self_employed', label: 'Self-employed' },
  { value: 'business_owner', label: 'Business owner' },
  { value: 'homemaker', label: 'Homemaker' },
  { value: 'retired', label: 'Retired' },
  { value: 'all', label: 'All occupations' },
];

export const OCCUPATIONS_ES = [
  { value: 'student', label: 'Estudiante' },
  { value: 'employee', label: 'Empleado' },
  { value: 'self_employed', label: 'Independiente' },
  { value: 'business_owner', label: 'Empresario' },
  { value: 'homemaker', label: 'Ama de casa' },
  { value: 'retired', label: 'Jubilado' },
  { value: 'all', label: 'Todos' },
];

export const MARITAL_STATUSES = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'partnered', label: 'Partnered' },
  { value: 'divorced', label: 'Divorced/Separated' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'all', label: 'All statuses' },
];

export const MARITAL_STATUSES_ES = [
  { value: 'single', label: 'Soltero/a' },
  { value: 'married', label: 'Casado/a' },
  { value: 'partnered', label: 'Unión libre' },
  { value: 'divorced', label: 'Divorciado/Separado' },
  { value: 'widowed', label: 'Viudo/a' },
  { value: 'all', label: 'Todos' },
];

export const LIFESTYLE_SEGMENTS = [
  { value: 'traditional', label: 'Traditional' },
  { value: 'modern', label: 'Modern/Urban' },
  { value: 'health_conscious', label: 'Health-conscious' },
  { value: 'price_focused', label: 'Price-focused' },
  { value: 'premium', label: 'Premium seekers' },
  { value: 'all', label: 'All lifestyles' },
];

export const LIFESTYLE_SEGMENTS_ES = [
  { value: 'traditional', label: 'Tradicional' },
  { value: 'modern', label: 'Moderno/Urbano' },
  { value: 'health_conscious', label: 'Consciente de salud' },
  { value: 'price_focused', label: 'Enfocado en precio' },
  { value: 'premium', label: 'Busca premium' },
  { value: 'all', label: 'Todos' },
];

// === SODA CONSUMPTION ===

export const CONSUMPTION_FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'several_weekly', label: 'Several times/week' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'occasional', label: 'Occasional' },
  { value: 'rarely', label: 'Rarely' },
  { value: 'all', label: 'All frequencies' },
];

export const CONSUMPTION_FREQUENCIES_ES = [
  { value: 'daily', label: 'Diario' },
  { value: 'several_weekly', label: 'Varias veces/semana' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'occasional', label: 'Ocasional' },
  { value: 'rarely', label: 'Raramente' },
  { value: 'all', label: 'Todas' },
];

// === CHANNEL ===

export const CHANNELS = [
  { value: 'traditional', label: 'Traditional (tiendita)' },
  { value: 'modern', label: 'Modern (supermarket)' },
  { value: 'convenience', label: 'Convenience (OXXO)' },
  { value: 'ontrade', label: 'On-trade (restaurants)' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'all', label: 'All channels' },
];

export const CHANNELS_ES = [
  { value: 'traditional', label: 'Tradicional (tiendita)' },
  { value: 'modern', label: 'Moderno (supermercado)' },
  { value: 'convenience', label: 'Conveniencia (OXXO)' },
  { value: 'ontrade', label: 'On-trade (restaurantes)' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'all', label: 'Todos' },
];

// === DECISION FACTORS (What consumers care about) ===

export const DECISION_FACTORS = {
  price: { en: 'Price/Value', es: 'Precio/Valor' },
  taste: { en: 'Taste/Flavor', es: 'Sabor' },
  brand: { en: 'Brand Trust', es: 'Confianza en Marca' },
  health: { en: 'Health/Nutrition', es: 'Salud/Nutrición' },
  convenience: { en: 'Convenience', es: 'Conveniencia' },
  availability: { en: 'Availability', es: 'Disponibilidad' },
  promotions: { en: 'Promotions/Deals', es: 'Promociones' },
  family: { en: 'Family Preference', es: 'Preferencia Familiar' },
  size: { en: 'Package Size', es: 'Tamaño de Empaque' },
  cold: { en: 'Cold Availability', es: 'Disponibilidad Frío' },
};

// === MEDIA CHANNELS (Where to reach consumers) ===

export const MEDIA_CHANNELS = {
  tv: { en: 'Television', es: 'Televisión' },
  social_media: { en: 'Social Media', es: 'Redes Sociales' },
  radio: { en: 'Radio/Podcasts', es: 'Radio/Podcasts' },
  ooh: { en: 'Outdoor/OOH', es: 'Espectaculares/OOH' },
  digital: { en: 'Digital Ads', es: 'Publicidad Digital' },
  pos: { en: 'Point of Sale', es: 'Punto de Venta' },
  influencers: { en: 'Influencers', es: 'Influencers' },
  word_of_mouth: { en: 'Word of Mouth', es: 'Boca a Boca' },
  streaming: { en: 'Streaming', es: 'Streaming' },
  print: { en: 'Print Media', es: 'Medios Impresos' },
};
